import { useCallback, useMemo, useRef, useState } from 'react';
import { bridgeApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Skeleton } from '@verlo/shared/components/ui/skeleton';
import { Textarea } from '@verlo/shared/components/ui/textarea';
import { FileSpreadsheet, Upload, Check, X, ClipboardPaste } from 'lucide-react';
import { parseMenu, menuCsvFile } from '../../lib/pastedMenu';
import { toast } from 'sonner';
import { usePageTitle } from '../../components/Layout';

/**
 * Bringing a venue's existing menu in (Verlo Bridge, restored 4 Oct 2026).
 *
 * The whole point is that nobody retypes a menu. They hand us the spreadsheet their last
 * till exported, or the one the kitchen keeps, and we do the work — guess what each column
 * is, say how confident we are, show exactly what will be created, and commit only the rows
 * somebody looked at and kept.
 *
 * Three rules this screen is built on, in order of how much they matter:
 *
 * 1. **Show the working.** A confident wrong import is worse than no import, so the mapping
 *    is on screen before anything is committed and every row can be unticked.
 * 2. **Say what is missing and what it costs them.** A file with no cost column imports
 *    perfectly well and leaves GP blank, and they should hear that from us now rather than
 *    find it in a report later.
 * 3. **Never half-import.** The commit settles every code first and writes nothing until it
 *    is sure, so a clash on row 30 cannot leave rows 1–29 on the menu.
 */

const gbp = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' })
  .format(Number(n) || 0);

//: The fields that change what a venue gets out of Verlo, and what it costs them to leave
//: one out. Anything not named here is a nice-to-have.
const LOAD_BEARING = [
  { field: 'name', label: 'Name', missing: "nothing can be imported without it" },
  { field: 'price', label: 'Price', missing: 'items will land at £0.00' },
  { field: 'cost', label: 'Cost price', missing: 'GP and margin will be blank for these dishes' },
  { field: 'category', label: 'Section', missing: 'everything lands in one section' },
];

const MenuImportPage = () => {
  usePageTitle('Import a menu');   // the top line has the title: no second one below
  const fileRef = useRef(null);
  const [record, setRecord] = useState(null);
  const [rows, setRows] = useState([]);
  const [dropped, setDropped] = useState(new Set());
  const [defaultCategory, setDefaultCategory] = useState('Imported');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  // The paste route. Open beside the file picker rather than behind it, because for a
  // venue whose menu is a PDF this is the only one of the two that works.
  const [pasting, setPasting] = useState(false);
  const [pasted, setPasted] = useState('');
  // Parsed as they type, so the button can say what it is about to do.
  const pastedRows = useMemo(() => parseMenu(pasted).rows, [pasted]);

  const mapping = record?.auto_mapped || {};
  const targets = new Set(Object.values(mapping));

  /** The rows endpoint hands back the file as it was written — each row keyed by the
   *  venue's own column names — plus the mapping it worked out. Projecting one through the
   *  other is the client's job, and it is what lets this screen show the dish exactly as it
   *  will land rather than as it was typed. */
  const read = useCallback(async (id) => {
    const { data } = await bridgeApi.getImportRows(id);
    const map = data.auto_mapped || {};
    setRows((data.rows || []).map((entry) => {
      const raw = entry.row || entry;
      const out = { _error: Boolean(entry.has_error) };
      Object.entries(map).forEach(([source, target]) => { out[target] = raw[source]; });
      return out;
    }));
  }, []);

  /** Pasted text, parsed here and handed to the same endpoint as a CSV.
   *
   *  Everything after this point — the column mapping, the validation, the row documents,
   *  the table below, the commit — cannot tell the difference, which is the whole reason
   *  this is one component and not a feature. */
  const usePasted = async () => {
    const rows = pastedRows;
    if (!rows.length) {
      toast.error('No dishes found. Each line wants a price on the end of it.');
      return;
    }
    await upload(menuCsvFile(rows));
    setPasting(false);
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setDone(null);
    try {
      const res = await bridgeApi.upload('menu', file);
      setRecord(res.data);
      setDropped(new Set());
      await read(res.data.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That file couldn't be read");
      setRecord(null);
      setRows([]);
    } finally {
      setBusy(false);
    }
  };

  const toggle = (i) => setDropped((prev) => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });

  const commit = async () => {
    setBusy(true);
    try {
      const accepted = rows.map((_, i) => i).filter((i) => !dropped.has(i));
      const res = await bridgeApi.commitToMenu(record.id, {
        accepted_row_indexes: accepted,
        default_category: defaultCategory || 'Imported',
      });
      setDone(res.data);
      toast.success(`${res.data.created} ${res.data.created === 1 ? 'dish' : 'dishes'} on the menu`);
    } catch (err) {
      // A clash names the codes, so it goes in front of them rather than into a toast that
      // disappears before it can be acted on.
      toast.error(err?.response?.data?.detail || "Couldn't add them to the menu", { duration: 8000 });
    } finally {
      setBusy(false);
    }
  };

  const keeping = rows.length - dropped.size;

  return (
    <div className="space-y-6" data-testid="menu-import-page">
      <div className="space-y-1">
        <p className="text-[14px] text-ink-soft">
          Upload the spreadsheet you already have: an export from your last till, or the
          list the kitchen keeps. We&rsquo;ll work out the columns and show you what we found
          before anything reaches the menu.
        </p>
      </div>

      {/* Upload */}
      <div className="rounded-lg border border-hairline bg-surface p-5">
        <input
          id="menu-import-file"
          ref={fileRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          className="sr-only"
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="menu-import-choose">
            <Upload size={15} className="mr-1.5" />
            {record ? 'Choose a different file' : 'Choose a file'}
          </Button>
          <Button variant="outline" onClick={() => setPasting((v) => !v)} disabled={busy}
                  data-testid="menu-import-paste-toggle">
            <ClipboardPaste size={15} className="mr-1.5" />
            Paste it instead
          </Button>
          <span className="text-[13px] text-ink-faint">
            {record
              ? <><FileSpreadsheet size={13} className="mr-1 inline" />{record.filename || 'spreadsheet'} · {record.row_count} rows</>
              : 'CSV or Excel, or paste from a PDF'}
          </span>
        </div>

        {/* The paste route. A menu copied out of a PDF arrives one dish per line with the
            price on the end, which is all this needs. */}
        {pasting && (
          <div className="mt-4 space-y-2 border-t border-hairline pt-4">
            <Label htmlFor="menu-paste" className="text-[12.5px]">
              Paste your menu: one dish a line, price on the end
            </Label>
            <Textarea
              id="menu-paste"
              rows={10}
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
              placeholder={'For example:\n\nSTARTERS\nSoup of the day ....... £6.50\nWith warm bread and butter\n\nMAINS\nFish and chips £15.00'}
              className="font-mono text-[13px]"
              data-testid="menu-import-paste"
            />
            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={usePasted} disabled={busy || !pasted.trim()}
                      data-testid="menu-import-paste-read">
                Read {pastedRows.length || ''} {pastedRows.length === 1 ? 'dish' : 'dishes'}
              </Button>
              <span className="text-[12.5px] text-ink-faint">
                A line with no price becomes the section above the dishes under it. You will
                see every row before anything reaches the menu.
              </span>
            </div>
          </div>
        )}
      </div>

      {busy && !record && <Skeleton className="h-40 w-full" />}

      {record && (
        <>
          {/* What we made of the columns */}
          <div className="rounded-lg border border-hairline bg-surface p-5 space-y-4" data-testid="menu-import-mapping">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="font-heading text-[15px] font-bold text-ink">What we made of your columns</h2>
              {/* How much of THEIR file we understood — not how many of the twelve fields a
                  menu item can have were filled. That reading of it put "42% matched" over a
                  spreadsheet whose every column had been mapped correctly, which is the one
                  thing the screen must not say to someone watching their menu go in. What is
                  missing, and what it costs them, is the Notice below — it already does that
                  job properly. */}
              <span className="font-mono text-[12.5px] tabular-nums text-ink-faint">
                {Object.keys(mapping).length} of {(record.columns || []).length} columns understood
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(mapping).map(([source, target]) => (
                <span key={source} className="border border-hairline-strong bg-surface-2 px-2 py-1 text-[12px]">
                  <span className="text-ink-faint">{source}</span>
                  <span className="mx-1.5 text-ink-faint">→</span>
                  <span className="font-semibold text-ink">{target.replace(/_/g, ' ')}</span>
                </span>
              ))}
            </div>

            {/* What's absent, and what it costs them — before they commit, not after. */}
            {LOAD_BEARING.some((f) => !targets.has(f.field)) && (
              <Notice tone="warn" word="Not in this file" className="text-[13px]">
                <ul className="space-y-0.5">
                  {LOAD_BEARING.filter((f) => !targets.has(f.field)).map((f) => (
                    <li key={f.field}><b>{f.label}</b>: {f.missing}.</li>
                  ))}
                </ul>
                You can import anyway and fill these in later.
              </Notice>
            )}

            {(record.errors || []).length > 0 && (
              <Notice tone="stop" word="Rows to look at" className="text-[13px]">
                {record.errors.slice(0, 5).map((e, i) => (
                  <div key={i}>Row {e.row}: {e.message}</div>
                ))}
                {record.errors.length > 5 && <div>…and {record.errors.length - 5} more.</div>}
              </Notice>
            )}

            {!targets.has('category') && (
              <div className="max-w-xs space-y-1.5">
                <Label className="text-[12.5px]">Put everything in this section</Label>
                <Input value={defaultCategory} onChange={(e) => setDefaultCategory(e.target.value)}
                       data-testid="menu-import-default-category" />
              </div>
            )}
          </div>

          {/* The rows, as they will land */}
          <div className="rounded-lg border border-hairline bg-surface" data-testid="menu-import-rows">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-hairline px-5 py-3">
              <h2 className="font-heading text-[15px] font-bold text-ink">
                {keeping} of {rows.length} will be added
              </h2>
              <span className="text-[12.5px] text-ink-faint">Untick anything you don&rsquo;t want</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13.5px]" style={{ minWidth: '520px' }}>
                <thead>
                  <tr className="text-left font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">
                    <th className="w-10 px-5 py-2" />
                    <th className="py-2 pr-4">Dish</th>
                    <th className="py-2 pr-4">Section</th>
                    <th className="py-2 pr-4 text-right">Price</th>
                    <th className="py-2 pr-4 text-right">Cost</th>
                    <th className="py-2 pr-5 text-right">PLU</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => {
                    const out = dropped.has(i);
                    return (
                      <tr key={i} className={`border-t border-hairline ${out ? 'opacity-45' : ''}`}
                          data-testid={`menu-import-row-${i}`}>
                        <td className="px-5 py-2">
                          <button type="button" onClick={() => toggle(i)}
                                  aria-pressed={!out}
                                  aria-label={out ? `Include ${row.name}` : `Leave out ${row.name}`}
                                  className={`grid h-5 w-5 place-items-center border-[1.5px] ${out ? 'border-hairline-strong bg-surface' : 'border-verlo bg-verlo text-white'}`}>
                            {out ? <X size={11} /> : <Check size={12} />}
                          </button>
                        </td>
                        <td className="py-2 pr-4 font-medium text-ink">
                          {row.name || <span className="text-signal-stop">no name, will be skipped</span>}
                          {row._error && <span className="ml-1.5 text-[11px] font-semibold text-signal-warn">check this row</span>}
                        </td>
                        <td className="py-2 pr-4 text-ink-soft">{row.category || defaultCategory}</td>
                        <td className="py-2 pr-4 text-right font-mono tabular-nums">{gbp(row.price)}</td>
                        <td className="py-2 pr-4 text-right font-mono tabular-nums text-ink-soft">
                          {row.cost ? gbp(row.cost) : <span className="text-ink-faint">—</span>}
                        </td>
                        <td className="py-2 pr-5 text-right font-mono tabular-nums text-ink-soft">
                          {row.plu || <span className="text-ink-faint">auto</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-hairline px-5 py-3">
              {done
                ? <Signal tone="ok">{done.created} added{done.skipped ? ` · ${done.skipped} skipped` : ''}</Signal>
                : <span className="text-[12.5px] text-ink-faint">Nothing is added until you press the button.</span>}
              <Button onClick={commit} disabled={busy || keeping === 0 || Boolean(done)}
                      className="bg-verlo hover:bg-verlo-hover" data-testid="menu-import-commit">
                {busy ? 'Adding…' : `Add ${keeping} to the menu`}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MenuImportPage;
